import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import {
  reduxForm,
  Field,
  change,
  formValueSelector,
  propTypes as formProps,
  unregisterField,
  registerField,
} from 'redux-form';
import { ButtonV2 } from '@zola/zola-ui';
import { toastsActions } from '@zola-helpers/client';

import { RenderCheckboxRadioField, RenderField, RenderTextareaField } from 'components/common/form';
import * as Validations from 'components/common/form/commonValidations';
import { updateWeddingAccount } from 'actions/WeddingActions';
import { updateUserStatus } from 'actions/UserActions';

import 'stylesheets/pages/settings/PasswordSettingsForm.less';

// Disable  camelcase rule for file to allow for snake case elements from reducer.
/* eslint  camelcase: 0 */

class PasswordSettingsForm extends Component {
  constructor(props) {
    super(props);
    this.handleTogglePasscode = this.handleTogglePasscode.bind(this);
    this.handleUpdatePasscodeSettings = this.handleUpdatePasscodeSettings.bind(this);
  }

  handleUpdatePasscodeSettings(formValues) {
    const {
      showSuccessNotification,
      showErrorNotification,
      onUpdatePasscodeSettings,
      account,
      handleUpdateUserStatus,
    } = this.props;
    const successMessage = 'Your password settings have been saved!';
    const errorMessage = 'There was a problem updating your settings.';
    const enable_passcode = formValues.hasPasscode === 'true';
    const passcode = enable_passcode ? formValues.passcode : null;
    const passcode_instructions = enable_passcode ? formValues.guestInstructions || null : null;
    const weddingAccountRequest = Object.assign({}, account, {
      passcode,
      passcode_instructions,
      enable_passcode,
    });

    handleUpdateUserStatus().then(userStatus => {
      if (userStatus.payload.status !== 'IDLE') {
        onUpdatePasscodeSettings(weddingAccountRequest)
          .then(showSuccessNotification(successMessage))
          .catch(() => showErrorNotification(errorMessage));
      } else {
        browserHistory.push(`/account/login?returnTo=${window.location.pathname}`);
      }
    });
  }

  handleTogglePasscode(boolString) {
    const { onTogglePasscode, onRegisterPasswordField, onUnregisterPasswordField } = this.props;
    const passcodeEnabled = boolString === 'true';
    if (passcodeEnabled) {
      onRegisterPasswordField();
    } else {
      onUnregisterPasswordField();
    }
    onTogglePasscode(boolString);
  }

  render() {
    const { pristine, valid, submitting, handleSubmit, hasPasscode, initialValues } = this.props;
    const hasPristinePasscode =
      typeof this.props.passcode === 'undefined' || this.props.passcode === '';
    const isDisabled =
      pristine ||
      !valid ||
      submitting ||
      (hasPristinePasscode && initialValues.hasPasscode === 'false' && hasPasscode === 'true');

    return (
      <form
        onSubmit={handleSubmit(this.handleUpdatePasscodeSettings)}
        className="row"
        id="password-settings-form"
      >
        <div className="col-xs-12 col-sm-8 padding-top">
          <span>
            Do you want your <strong>wedding website</strong> password protected?
          </span>
        </div>
        <div className="col-xs-12 col-sm-4 padding-top margin-bottom">
          <Field
            value="true"
            component={RenderCheckboxRadioField}
            id="password-yes"
            name="hasPasscode"
            type="radio"
            label="Yes"
            inline
            checked
            styleOverride
            onChangeAction={() => this.handleTogglePasscode('true')}
          />
          <Field
            value="false"
            component={RenderCheckboxRadioField}
            id="password-no"
            name="hasPasscode"
            type="radio"
            label="No"
            inline
            checked
            styleOverride
            onChangeAction={() => this.handleTogglePasscode('false')}
          />
        </div>
        <div className={hasPasscode === 'true' ? '' : 'hidden'}>
          <div className="col-xs-12 padding-top">
            <div className="title">
              <span>
                Enter Password <span className="text-danger danger-asterisk">*</span>
              </span>
            </div>
            <Field
              className="input-field"
              type="text"
              component={RenderField}
              name="passcode"
              id="passcode"
              placeholder="Password"
              validate={
                hasPasscode === 'true' ? [Validations.REQUIRED, Validations.MAX_LENGTH_20] : []
              }
              maxChars={20}
            />
          </div>
          <div className="col-xs-12 padding-top">
            <div className="title">
              <span>Instructions for guests</span>
            </div>
            <Field
              className="input-field"
              hideFormGroup
              component={RenderTextareaField}
              name="guestInstructions"
              placeholder="If you don't know the password, check your invitation. Otherwise, reach out to Jen Foster."
              id="passcode-note"
            />
          </div>
        </div>
        <div className="col-xs-12 padding-top">
          <ButtonV2 type="submit" className="margin-top" disabled={isDisabled}>
            Save Changes
          </ButtonV2>
        </div>
      </form>
    );
  }
}

PasswordSettingsForm.propTypes = {
  passcode: PropTypes.string,
  hasPasscode: PropTypes.string,
  onTogglePasscode: PropTypes.func,
  onUpdatePasscodeSettings: PropTypes.func,
  showErrorNotification: PropTypes.func,
  showSuccessNotification: PropTypes.func,
  onUnregisterPasswordField: PropTypes.func,
  onRegisterPasswordField: PropTypes.func,
  handleUpdateUserStatus: PropTypes.func,
  ...formProps,
};

const selector = formValueSelector('passwordSettingsForm');

const mapStateToProps = state => {
  const { account } = state.wedding;
  const { passcode, passcode_instructions } = account;
  let { enable_passcode } = account;
  // Check must be performed to prevent errors during initial renders before the store fetches account state.
  if (!enable_passcode) {
    enable_passcode = false;
  }
  return {
    passcode: selector(state, 'passcode'),
    hasPasscode: selector(state, 'hasPasscode'),
    initialValues: {
      hasPasscode: enable_passcode.toString(),
      passcode,
      guestInstructions: passcode_instructions,
    },
    account: state.wedding.account,
  };
};

const mapDispatchToProps = dispatch => ({
  onTogglePasscode: isEnabled => dispatch(change('passwordSettingsForm', 'hasPasscode', isEnabled)),
  onUpdatePasscodeSettings: request => dispatch(updateWeddingAccount(request)),
  showSuccessNotification: headline => dispatch(toastsActions.positive({ headline })),
  showErrorNotification: headline => dispatch(toastsActions.negative({ headline })),
  onUnregisterPasswordField: () => dispatch(unregisterField('passwordSettingsForm', 'passcode')),
  onRegisterPasswordField: () =>
    dispatch(registerField('passwordSettingsForm', 'passcode', 'Field')),
  handleUpdateUserStatus: () => dispatch(updateUserStatus()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: 'passwordSettingsForm',
    enableReinitialize: true,
  })(PasswordSettingsForm)
);



// WEBPACK FOOTER //
// ./src/pages/settings/privacy/components/PasswordSettingsForm.jsx